/**
 * Tek bir sohbet balonu.
 *
 * Kullanıcı mesajı sağda ve düz metin; AI yanıtı solda ve Markdown. Akış sürerken
 * metin DÜZ gösteriliyor, yanıt bitince bir kez Markdown'a çevriliyor: yarım kalmış
 * tablo/kod bloğu her parçada yeniden ayrıştırılınca satırlar zıplıyordu.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, FileText, Paperclip } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton';
import { OctoMark } from '@/shared/ui/OctoMark';
import type { ChatMessage } from '@/shared/types/aiChat';
import { renderMarkdown } from '../model/markdown';
import { AiArtifactCard } from './AiArtifactCard';

export function AiMessageBubble({ message }: { message: ChatMessage }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const html = useMemo(
    () => (isUser || message.isStreaming ? null : renderMarkdown(message.content)),
    [isUser, message.isStreaming, message.content],
  );

  const copy = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(message.content);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      } catch {
        /* pano erişimi yoksa metin zaten ekranda */
      }
    })();
  };

  return (
    <div className={cn('group flex gap-2', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser ? (
        <span className="bg-brand-500/10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
          <OctoMark className="h-4 w-4" title="Octo AI" />
        </span>
      ) : null}

      <div className={cn('min-w-0', isUser ? 'max-w-[85%]' : 'w-full max-w-full')}>
        <div
          className={cn(
            'rounded-2xl px-3 py-2 text-sm',
            isUser ? 'bg-brand-600 rounded-br-sm text-white' : 'bg-bg-muted text-fg rounded-bl-sm',
            message.error && !isUser && 'border-danger/40 border',
          )}
        >
          {html !== null ? (
            <div
              className="octo-ai-markdown break-words [&_a]:underline [&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_h1]:mt-2 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mt-2 [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:font-medium [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-black/80 [&_pre]:p-2 [&_pre]:text-xs [&_pre]:text-white [&_table]:my-2 [&_table]:block [&_table]:w-max [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:text-xs [&_td]:border [&_td]:border-black/10 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-black/10 [&_th]:px-2 [&_th]:py-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <p className="break-words whitespace-pre-wrap">
              {message.content}
              {message.isStreaming ? <span className="octo-ai-caret" aria-hidden="true" /> : null}
            </p>
          )}

          {message.attachments?.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {message.attachments.map((a) => (
                <span
                  key={a.filePath}
                  className={cn(
                    'inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px]',
                    isUser ? 'bg-white/20' : 'bg-bg-elevated',
                  )}
                >
                  <Paperclip className="h-3 w-3 shrink-0" />
                  <span className="truncate">{a.fileName}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* AI'ın ürettiği raporlar balonun ALTINDA: uzun belge konuşmayı bastırmasın. */}
        {message.artifacts?.map((artifact) => (
          <AiArtifactCard key={artifact.fileName} artifact={artifact} />
        ))}

        {!isUser && !message.isStreaming && message.content ? (
          <div className="mt-0.5 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <IconButton label={t('ai.copy')} size="sm" onClick={copy}>
              {copied ? <Check className="text-success h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </IconButton>
            {message.artifacts?.length ? (
              <span className="text-fg-subtle inline-flex items-center gap-1 text-[11px]">
                <FileText className="h-3 w-3" />
                {t('ai.artifact.count', { count: message.artifacts.length })}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
